import IChatMessage from "../types/interfaces/IChatMessage";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Snackbar,
  Toolbar,
} from "@mui/material";
import ComposeArea, { ComposeAreaProps } from "./chat/ComposeArea";
import ChatHistory, { ChatHistoryProps } from "./chat/ChatHistory";
import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import sendGatewayRequest from "../functions/requests/sendGatewayRequest";
import { useOidcAccessToken } from "@axa-fr/react-oidc";
import readMessageStream from "../functions/requests/readMessageStream";
import { MessageRole } from "../types/enums/MessageRole";
import ConversationSettings from "./ConversationSettings";
import IConversationSettings from "../types/interfaces/IConversationSettings";
import IModel from "../types/interfaces/IModel";
import { Close } from "@mui/icons-material";
import sendImageGatewayRequest from "../functions/requests/sendImageGatewayRequest";
import { withTransaction } from "@elastic/apm-rum-react";
import { apm } from "@elastic/apm-rum";
import getDefaultModelFromList from "../functions/comparisons-arrays/getDefaultModelFromList";
import { OidcConfigurationNameContext } from "../contexts/OidcConfigurationNameContext";
import { DarkModeContext } from "../contexts/DarkModeContext";
import { useSending } from "../hooks/useSending";
import { useAvailableModels } from "../hooks/useAvailableModels";
import { useSidebarOpen } from "../hooks/useSidebarOpen";
import { useFormFactor } from "../hooks/useFormFactor";
import { FormFactor } from "../types/enums/FormFactor";
import { useChatOptions } from "../hooks/useChatOptions";
import {
  useChatMessageListQuery,
  usePaginatedConversationList
} from "../hooks/useConversationListQuery";
import {
  PersistenceAPI,
  PersistenceThread,
} from "../functions/requests/persistence";
import { useMutation, useQueryClient } from "react-query";
import { OidcAccessToken } from "@axa-fr/react-oidc/dist/ReactOidc";
import { submitFeedback } from "../functions/requests/submitFeedback";
import cleanGatewayResponse from "../functions/requests/cleanGatewayResponse";
import { IncomingMessageStream } from "./chat/IncomingMessage";
import { capitalizeConversationTitle } from "../utils/textUtils";

export default withTransaction(
  "ActiveConversationWindow",
  "component",
)(ActiveConversationWindow);

export type ActiveConversationWindowProps = {
  conversationId: string | null | undefined;
  onTranscriptDownloaded: (history: IChatMessage[]) => void;
  defaultSystemPrompt: string;
  useRagByDefault: boolean;
  onLoadingFailed: () => void;
};

function ActiveConversationWindow({
  conversationId,
  onTranscriptDownloaded,
  defaultSystemPrompt,
  useRagByDefault,
    onLoadingFailed,
}: ActiveConversationWindowProps) {
  const { availableModels } = useAvailableModels();
  const persistenceChatMessages = useChatMessageListQuery(
    conversationId,
    availableModels,
  );
  const { isSending, setSending, clearSending } = useSending();
  const { sidebarOpen, sidebarWidth } = useSidebarOpen();
  const paginatedConversationList = usePaginatedConversationList(50);
  const { threads: conversations, updateThread, forceRefresh } = paginatedConversationList;
  const formFactor = useFormFactor();
  const [stream, setStream] = useState<ChatHistoryProps["stream"]>();
  const [conversationSettingsOpen, setConversationSettingsOpen] =
    useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarText, setSnackbarText] = useState<string>("No notification");
  const { darkModeEnabled } = useContext(DarkModeContext);
  const { oidcConfigurationName } = useContext(OidcConfigurationNameContext);
  const abortControllerRef = useRef<AbortController | null>(null);
  const abort = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);
  const streamedContentRef = useRef<string>("");
  const accessToken = useOidcAccessToken(oidcConfigurationName);
  const { chatOptions } = useChatOptions();
  const queryClient = useQueryClient();

  // Add a ref at the component level to track first render
  const isFirstSettingsRender = useRef(true);
  const hasLoggedInitialSettings = useRef(false);

  // Add a more sophisticated tracking mechanism at component level
  const deletedMessagesContent = useRef<Set<string>>(new Set());
  // Keep the existing deletedMessageIds
  const deletedMessageIds = useRef<Set<string>>(new Set());
  
  // Add function to persist deleted message tracking to localStorage
  const persistDeletedMessages = useCallback((conversationId: string) => {
    if (!conversationId) return;
    
    // Create a storage key specific to this conversation
    const storageKey = `deleted-messages-${conversationId}`;
    
    // Convert sets to arrays for storage
    const dataToStore = {
      ids: Array.from(deletedMessageIds.current),
      contents: Array.from(deletedMessagesContent.current)
    };
    
    // Save to localStorage
    try {
      localStorage.setItem(storageKey, JSON.stringify(dataToStore));
      console.log(`Persisted deleted message tracking for conversation ${conversationId}`);
    } catch (error) {
      console.error("Failed to persist deleted message tracking:", error);
    }
  }, []);
  
  // Add function to load deleted message tracking from localStorage
  const loadDeletedMessages = useCallback((conversationId: string) => {
    if (!conversationId) return;
    
    // Create a storage key specific to this conversation
    const storageKey = `deleted-messages-${conversationId}`;
    
    try {
      const storedData = localStorage.getItem(storageKey);
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        
        // Clear current sets
        deletedMessageIds.current.clear();
        deletedMessagesContent.current.clear();
        
        // Populate from storage
        if (parsedData.ids && Array.isArray(parsedData.ids)) {
          parsedData.ids.forEach((id: string) => deletedMessageIds.current.add(id));
        }
        
        if (parsedData.contents && Array.isArray(parsedData.contents)) {
          parsedData.contents.forEach((content: string) => deletedMessagesContent.current.add(content));
        }
        
        console.log(`Loaded deleted message tracking for conversation ${conversationId}`, {
          ids: deletedMessageIds.current.size,
          contents: deletedMessagesContent.current.size
        });
      }
    } catch (error) {
      console.error("Failed to load deleted message tracking:", error);
    }
  }, []);

  // Create a separate state for regeneration
  const [regeneratingState, setRegeneratingState] = useState({
    isRegenerating: false,
    streamContent: "",
    regeneratedContent: null as string | null
  });

  // Add regenerating ref to track state
  const regenerating = useRef<boolean>(false);

  // Keep track of the message being actively regenerated
  const messageBeingRegeneratedId = useRef<string | null>(null);
  const messageBeingRegeneratedContent = useRef<string | null>(null);

  const editConversationApi = useMutation({
    mutationFn: async (args: {
      accessToken: OidcAccessToken;
      editedThread: PersistenceThread;
    }) => {
      if (args.editedThread.id === undefined) return;
      
      console.log("Updating thread in API:", {
        id: args.editedThread.id,
        name: args.editedThread.name,
        model: args.editedThread.model
      });
      
      // Don't allow unnamed conversations unless it's explicitly being set
      // This prevents accidental resets of conversation names
      if (args.editedThread.name === "Unnamed Conversation") {
        // Verify if this is an existing thread with a real name
        try {
          const existingThread = await PersistenceAPI.getThreadById(
            args.accessToken.accessToken,
            args.editedThread.id
          );
          
          if (existingThread && existingThread.name && 
              existingThread.name !== "Unnamed Conversation") {
            console.log(`Preserving existing thread name "${existingThread.name}" instead of resetting to "Unnamed Conversation"`);
            args.editedThread.name = existingThread.name;
          }
        } catch (error) {
          console.warn("Error checking existing thread name:", error);
          // Continue with the update even if this check fails
        }
      }
      
      // Add timestamp to ensure changes are detected
      const threadWithTimestamp = {
        ...args.editedThread,
        updated_at: new Date().toISOString()
      };
      
      // Ensure ID is defined before using it
      const threadId = threadWithTimestamp.id;
      if (!threadId) {
        console.error("Cannot update thread: ID is undefined");
        return;
      }
      
      // Use retry logic to handle transient errors
      let attempts = 0;
      const maxAttempts = 3;
      
      while (attempts < maxAttempts) {
        try {
          attempts++;
          const result = await PersistenceAPI.updateThread(
            args.accessToken.accessToken,
            threadId, // Now using our safely checked ID
            threadWithTimestamp
          );
          
          // Verify the result
          if (result && result.name === args.editedThread.name) {
            console.log(`Thread update successful (attempt ${attempts})`);
            
            // Also update in paginated list
            if (result) {
              updateThread(result);
            }
            
            return result;
          } else {
            console.warn("Thread update did not return expected result:", result);
            // Short delay before retry
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        } catch (error) {
          console.error(`Error updating thread (attempt ${attempts}/${maxAttempts}):`, error);
          // Only retry on specific errors
          if (attempts < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, 500));
          } else {
            throw error; // Rethrow on final attempt
          }
        }
      }
      
      // Default fallback for final attempt
      const result = await PersistenceAPI.updateThread(
        args.accessToken.accessToken,
        threadId, // Using our safely checked ID
        threadWithTimestamp
      );
      
      // Also update in paginated list
      if (result) {
        updateThread(result);
      }
      
      return result;
    },
    onSuccess: (result: PersistenceThread | undefined, variables: { accessToken: OidcAccessToken; editedThread: PersistenceThread }) => {
      if (result && variables.editedThread.id) {
        // Double-check that the update was applied
        setTimeout(async () => {
          try {
            // Ensure ID is defined
            const threadId = variables.editedThread.id;
            if (!threadId) {
              console.warn("Cannot verify thread update: ID is undefined");
              return;
            }
            
            const verifiedThread = await PersistenceAPI.getThreadById(
              variables.accessToken.accessToken,
              threadId
            );
            
            // Check if the update was properly persisted
            if (verifiedThread && verifiedThread.name !== variables.editedThread.name) {
              console.warn("Thread name verification failed - expected:", 
                variables.editedThread.name, "got:", verifiedThread.name);
              
              // One final attempt to fix the name
              try {
                const finalUpdate = await PersistenceAPI.updateThread(
                  variables.accessToken.accessToken,
                  threadId,
                  {
                    ...verifiedThread,
                    name: variables.editedThread.name,
                    updated_at: new Date().toISOString()
                  }
                );
                
                if (finalUpdate) {
                  console.log("Final thread update applied successfully");
                  updateThread(finalUpdate);
                }
              } catch (finalError) {
                console.error("Final thread update failed:", finalError);
              }
            } else {
              console.log("Thread update verification successful");
            }
          } catch (verifyError) {
            console.error("Error verifying thread update:", verifyError);
          }
        }, 2000);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["paginatedConversationList"] });
    },
  });

  const addMessageApi = useMutation({
    mutationFn: async (args: {
      chatMessage: IChatMessage;
      accessToken: OidcAccessToken;
    }) => {
      return PersistenceAPI.createMessageFromChatMessage(
        args.accessToken.accessToken,
        args.chatMessage,
      );
    },
    onMutate: async (args: {
      chatMessage: IChatMessage;
      accessToken: OidcAccessToken;
    }) => {
      await queryClient.cancelQueries({
        queryKey: ["chatMessageList", conversationId],
      });
      const previousMessages = queryClient.getQueryData([
        "chatMessageList",
        conversationId,
      ]);
      queryClient.setQueryData(
        ["chatMessageList", conversationId],
        (old: any) => [...old, args.chatMessage],
      );
      return { previousMessages };
    },
    onError: (err, chatMessage, context) => {
      queryClient.setQueryData(
        ["chatMessageList", conversationId],
        context?.previousMessages,
      );
      onLoadingFailed();
    },
    onSettled: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["chatMessageList", conversationId],
        refetchActive: false,
      });
    },
  });

  const deleteMessageApi = useMutation({
    mutationFn: async (args: {
      chatMessage: IChatMessage;
      accessToken: OidcAccessToken;
    }) => {
      console.log(`Deleting message with ID: ${args.chatMessage.id}`);
      return PersistenceAPI.deleteMessage(
        args.accessToken.accessToken,
        args.chatMessage.id,
      );
    },
    onMutate: async (args: {
      chatMessage: IChatMessage;
      accessToken: OidcAccessToken;
    }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ["chatMessageList", conversationId],
      });
      
      // Snapshot the previous value
      const previousMessages: IChatMessage[] | undefined =
        queryClient.getQueryData(["chatMessageList", conversationId]);
        
      console.log(`Optimistically removing message ${args.chatMessage.id} from UI`);
      
      // Optimistically update the cache
      queryClient.setQueryData(
        ["chatMessageList", conversationId],
        (old: IChatMessage[] | undefined) => 
          (old || []).filter((m: IChatMessage) => m.id !== args.chatMessage.id)
      );
      
      // Also add to deletion tracking for persistence
      if (args.chatMessage.id) {
        deletedMessageIds.current.add(args.chatMessage.id);
      }
      
      if (args.chatMessage.content) {
        deletedMessagesContent.current.add(args.chatMessage.content);
      }
      
      // Save deletion tracking to localStorage
      if (conversationId) {
        persistDeletedMessages(conversationId);
      }
      
      return { previousMessages };
    },
    onError: (err, chatMessage, context) => {
      console.error("Error deleting message:", err);
      // Revert to previous state on error
      queryClient.setQueryData(
        ["chatMessageList", conversationId],
        context?.previousMessages
      );
      
      // Show error notification
      handleSnackbarOpen("Error deleting message");
    },
    onSuccess: (data, variables) => {
      console.log("Message deletion successful:", data);
      
      // Force update component state
      const cachedMessages = queryClient.getQueryData<IChatMessage[]>(
        ["chatMessageList", conversationId]
      );
      
      if (cachedMessages) {
        // Re-apply filtering to ensure message is removed
        const filteredMessages = cachedMessages.filter(
          msg => msg.id !== variables.chatMessage.id
        );
        
        // Update cache with filtered messages
        queryClient.setQueryData(
          ["chatMessageList", conversationId],
          filteredMessages
        );
      }
    },
    onSettled: () => {
      console.log("Message deletion settled, invalidating queries");
      // Always invalidate queries when settled
      queryClient.invalidateQueries({
        queryKey: ["chatMessageList", conversationId],
      });
    },
  });

  // Add back the updateMessageApi mutation that was accidentally removed
  const updateMessageApi = useMutation({
    mutationFn: async (args: {
      chatMessage: IChatMessage;
      accessToken: OidcAccessToken;
    }) => {
      console.log(`Updating message with ID: ${args.chatMessage.id}`);
      return PersistenceAPI.updateMessageFromChatMessage(
        args.accessToken.accessToken,
        args.chatMessage,
      );
    },
    onMutate: async (args: {
      chatMessage: IChatMessage;
      accessToken: OidcAccessToken;
    }) => {
      await queryClient.cancelQueries({
        queryKey: ["chatMessageList", conversationId],
      });
      const previousMessages: IChatMessage[] | undefined =
        queryClient.getQueryData(["chatMessageList", conversationId]);
      
      // Optimistically update the cache
      queryClient.setQueryData(
        ["chatMessageList", conversationId],
        (old: IChatMessage[] | undefined) => {
          if (!old) return [args.chatMessage];
          return old.map(msg => 
            msg.id === args.chatMessage.id ? args.chatMessage : msg
          );
        }
      );
      
      return { previousMessages };
    },
    onError: (err, chatMessage, context) => {
      console.error("Error updating message:", err);
      queryClient.setQueryData(
        ["chatMessageList", conversationId],
        context?.previousMessages,
      );
      
      // Show error notification
      handleSnackbarOpen("Error updating message");
    },
    onSuccess: (data, variables) => {
      console.log("Message update successful:", data);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["chatMessageList", conversationId],
      });
    },
  });

  // Replace the global override with a conversation-specific Map
  const modelOverridesByConversation = useRef<Map<string, IModel>>(new Map());

  // Limit the frequency of model override logging 
  const lastModelLogTime = useRef<Record<string, number>>({});

  // Update the currentConversationSettings to only use the override for the current conversation
  const currentConversationSettings = useCallback((): IConversationSettings => {
    let currentConversation = conversations?.find(
      (c) => c.id === conversationId,
    );
    
    // Only log on the first meaningful render with data - not during initial loading
    if (isFirstSettingsRender.current && conversationId && !hasLoggedInitialSettings.current) {
      // Only log when there's an actual conversation available
      if (currentConversation || conversationId) {
        console.log("Current conversation in settings:", 
          currentConversation ? {
            id: currentConversation.id,
            model: currentConversation.model,
            name: currentConversation.name
          } : "not found");
          
        // Mark that we've logged for this conversation
        hasLoggedInitialSettings.current = true;
          
        // Reset after a delay to allow logging for new conversations
        setTimeout(() => {
          hasLoggedInitialSettings.current = false;
        }, 1000);
      }
    }
    
    // Always use the default model for a new conversation or when model isn't specified
    const defaultModel = getDefaultModelFromList(availableModels);
    
    // If there's no system prompt or it's empty, use the default system prompt
    const systemPromptToUse = 
      !currentConversation?.instructions || currentConversation.instructions.trim() === "" 
        ? defaultSystemPrompt 
        : currentConversation.instructions;
    
    // IMPORTANT: First check for conversation-specific model override
    let selectedModel;
    if (conversationId && modelOverridesByConversation.current.has(conversationId)) {
      // Use the conversation-specific override
      selectedModel = modelOverridesByConversation.current.get(conversationId) || defaultModel;
      
      // Limit logging to once per second to avoid spamming the console
      const now = Date.now();
      const lastLogTime = lastModelLogTime.current[conversationId] || 0;
      if (now - lastLogTime > 2000) {  // Log at most once every 2 seconds
        console.log(`Using model override for conversation ${conversationId}: ${selectedModel?.internal_name}`);
        lastModelLogTime.current[conversationId] = now;
      }
    } else if (currentConversation?.model) {
      // Then try the model from the conversation data
      selectedModel = availableModels.find(
        (m) => m.internal_name === currentConversation?.model,
      ) ?? defaultModel;
    } else {
      // Fallback to default model
      selectedModel = defaultModel;
    }
    
    return {
      systemPrompt: systemPromptToUse,
      rag: currentConversation?.rag ?? [],
      pii: currentConversation?.pii ?? false,
      model: selectedModel!,
    };
  }, [conversationId, conversations, availableModels, defaultSystemPrompt]);

  // Use the useMemo hook to memoize the current model
  // This prevents the model from changing unless actual settings have changed
  const currentModel = useMemo(() => {
    return currentConversationSettings().model;
  }, [currentConversationSettings]);

  const handleFeedback = async (
    messageId: string,
    feedback: boolean,
    details: string,
    accessToken: OidcAccessToken,
  ): Promise<void> => {
    await submitFeedback(messageId, feedback, details, accessToken);

    const selectedMessage = persistenceChatMessages.data?.find(
      (m) => m.id === messageId,
    );
    if (selectedMessage === undefined) {
      return Promise.resolve();
    }
    await updateMessageApi.mutateAsync({
      chatMessage: { ...selectedMessage, hasFeedback: true },
      accessToken: accessToken,
    });
  };

  // Prevent complete message re-fetches when only the model changes
  const updatePersistenceSystemPrompt = useCallback(
    (newPrompt: string) => {
      (async function () {
        if (conversationId === null || conversationId === undefined) {
          return;
        }
        try {
          const systemMessage = persistenceChatMessages.data?.find(
            (r) => r.role == MessageRole.System,
          );
          if (systemMessage === undefined) {
            console.error(
              `No such message: System message for ${conversationId}`,
            );
            return;
          }
          
          // Create a new system message instead of updating the existing one
          // This avoids the 405 Method Not Allowed error
          console.log("Creating new system message with updated prompt");
          
          try {
            // IMPORTANT: Save the current messages before updating
            const currentMessages = queryClient.getQueryData(["chatMessageList", conversationId]) as IChatMessage[] || [];
            
            // First delete the existing system message
            if (systemMessage.id) {
              await deleteMessageApi.mutateAsync({
                accessToken: accessToken,
                chatMessage: systemMessage,
              });
            }
            
            // Then create a new system message with the updated prompt
            await addMessageApi.mutateAsync({
              accessToken: accessToken,
              chatMessage: {
                conversationId: conversationId,
                role: MessageRole.System,
                id: "", // This will be assigned by the server
                content: newPrompt,
                updated: new Date(),
                model: systemMessage.model || getDefaultModelFromList(availableModels),
                hasFeedback: false,
              },
            });
            
            console.log("System message updated successfully");
            
            // AVOID FULL REFETCH: Instead of invalidating the entire query and refetching,
            // we'll use a targeted update approach
            const updatedMessages = await PersistenceAPI.getMessagesInThreadAsChatMessages(
              accessToken.accessToken,
              conversationId,
              availableModels
            );
            
            // Manually update the cache without triggering a full re-render
            queryClient.setQueryData(
              ["chatMessageList", conversationId],
              updatedMessages
            );
          } catch (updateError) {
            console.error("Error updating system message:", updateError);
          }
        } catch (error) {
          console.error("Error in updatePersistenceSystemPrompt:", error);
        }
      })();
    },
    [conversationId, persistenceChatMessages.data, accessToken, availableModels, queryClient]
  );

  useEffect(() => {
    document.documentElement.scrollTop = 0;
  }, [conversationId]);

  const updateStreamedMessageText = (chunk: string) => {
    streamedContentRef.current = streamedContentRef.current += chunk;
  };

  // Properly memoize the regenerate function to prevent it from changing identity on re-renders
  const handleRegeneratePersistenceMessage = useCallback(async () => {
    console.log("Starting regeneration with sequential DELETE + verification approach");
    if (!conversationId || regenerating.current) {
      console.log("Regeneration already in progress or no conversation ID");
      return;
    }
    regenerating.current = true;

    if (!persistenceChatMessages.data) {
      console.error("No messages to regenerate");
      regenerating.current = false;
      return;
    }

    try {
      // Set UI to regenerating state FIRST
      console.log("Setting regeneration state to active");
      setRegeneratingState({
        isRegenerating: true,
        streamContent: "",
        regeneratedContent: null
      });

      // Step 1: Find the most recent user message and assistant message
      const messages = [...persistenceChatMessages.data];
      console.log(`Total messages: ${messages.length}`);
      
      const lastAssistantMessageIndex = messages
        .map((m) => m.role)
        .lastIndexOf(MessageRole.Assistant);
      
      if (lastAssistantMessageIndex === -1) {
        console.error("No assistant message to regenerate");
        regenerating.current = false;
        setRegeneratingState({
          isRegenerating: false,
          streamContent: "",
          regeneratedContent: null
        });
        return;
      }

      // Get the assistant message we'll delete
      const assistantMessage = messages[lastAssistantMessageIndex];
      console.log(`Will delete assistant message: ${assistantMessage.id}`);
      
      // Store the message ID and content to track during regeneration
      messageBeingRegeneratedId.current = assistantMessage.id || null;
      messageBeingRegeneratedContent.current = assistantMessage.content || null;
    
      // STEP 1: Remove from UI immediately (we'll still use our localStorage tracking as backup)
      if (assistantMessage.id) {
        console.log(`Adding message ID ${assistantMessage.id} to deleted ID list`);
        deletedMessageIds.current.add(assistantMessage.id);
      }
      
      if (assistantMessage.content) {
        console.log(`Adding content signature to deletion tracking`);
        deletedMessagesContent.current.add(assistantMessage.content);
      }
      
      // Persist to localStorage as a backup
      if (conversationId) {
        persistDeletedMessages(conversationId);
      }
      
      // Immediately update the UI by modifying the query cache
      const currentMessages = queryClient.getQueryData(["chatMessageList", conversationId]) as IChatMessage[] || [];
      const filteredMessages = currentMessages.filter(msg => 
        msg.id !== assistantMessage.id && 
        !(msg.role === MessageRole.Assistant && msg.content === assistantMessage.content)
      );
      queryClient.setQueryData(["chatMessageList", conversationId], filteredMessages);
      console.log("Updated UI to hide message");
      
      // Find the user message that prompted this (to regenerate from)
      const userMessages = messages.filter(m => m.role === MessageRole.User);
      if (userMessages.length === 0) {
        console.error("No user messages found to regenerate from");
        regenerating.current = false;
        setRegeneratingState({
          isRegenerating: false,
          streamContent: "",
          regeneratedContent: null
        });
        return;
      }
      
      // Use the last user message
      const lastUserMessage = userMessages[userMessages.length - 1];
      console.log(`Regenerating based on user message: "${lastUserMessage.content.substring(0, 30)}..."`);
      
      // STEP 2: Send DELETE request to API (only if it has a real ID)
      // Skip if the message has a temp ID (!!TEMP) as it may not exist on the backend
      if (assistantMessage.id && assistantMessage.id !== "!!TEMP") {
        console.log(`Sending DELETE request for message ID: ${assistantMessage.id}`);
        try {
          const deleteResult = await PersistenceAPI.deleteMessage(
            accessToken.accessToken,
            assistantMessage.id
          );
          console.log("DELETE result:", deleteResult);
        } catch (deleteError) {
          console.error("Error deleting message:", deleteError);
          // Continue despite error - we'll still try regeneration
        }
      } else {
        console.log("Message has temporary ID - skipping DELETE request");
      }
      
      // STEP 3: Poll until the message is gone or max attempts reached
      let isMessageGone = false;
      const maxPollAttempts = 5;
      let pollAttempt = 0;
      
      // Only poll if we have a real conversation ID
      if (conversationId && assistantMessage.id && assistantMessage.id !== "!!TEMP") {
        console.log("Beginning polling to verify message deletion...");
        
        while (pollAttempt < maxPollAttempts && !isMessageGone) {
          pollAttempt++;
          console.log(`Polling attempt ${pollAttempt}/${maxPollAttempts}`);
          
          try {
            // Wait 1 second between attempts
            if (pollAttempt > 1) {
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
            
            // Get the thread messages directly using the correct API method
            try {
              const threadMessages = await PersistenceAPI.getMessagesInThread(
                accessToken.accessToken,
                conversationId
              );
              
              // Check if the message still exists
              const messageExists = threadMessages.some(
                (msg) => msg.id === assistantMessage.id
              );
              
              if (!messageExists) {
                console.log("Message successfully deleted from backend");
                isMessageGone = true;
              } else {
                console.log("Message still exists on backend");
              }
            } catch (fetchError) {
              console.error("Error fetching thread messages:", fetchError);
            }
          } catch (pollError) {
            console.error(`Error during poll attempt ${pollAttempt}:`, pollError);
          }
        }
        
        if (!isMessageGone) {
          console.warn("Max polling attempts reached - proceeding anyway");
        }
      }
      
      // STEP 4: Use current model to retry same prompt
      // Get the latest model selection in case it was changed
      const currentSettings = currentConversationSettings();
      const selectedModel = currentSettings.model || getDefaultModelFromList(availableModels);
      
      console.log(`Using current model: ${selectedModel.internal_name} for regeneration`);
      
      // Get system message for context
      const systemMessage = messages.find(m => m.role === MessageRole.System);
      
      // Only include system and user messages
      const messagesForRequest = [];
      if (systemMessage) messagesForRequest.push(systemMessage);
      messagesForRequest.push(lastUserMessage);
      
      console.log(`Sending request with ${messagesForRequest.length} messages`);
      
      // Send request to gateway with clean context
      const requestPromise = !selectedModel.image_model
        ? sendGatewayRequest(
            messagesForRequest,
            selectedModel,
            accessToken,
            currentSettings.rag,
            currentSettings.pii,
            chatOptions.show_rag,
            handleSnackbarOpen
          )
        : sendImageGatewayRequest(
            messagesForRequest,
            selectedModel,
            accessToken,
            currentSettings.rag,
            currentSettings.pii,
            handleSnackbarOpen
          );

      console.log("Sent gateway request");
      
      // Process the response
      const response = await requestPromise;
      console.log(`Got response: ${response.status}`);
      
      if (response.body === null) {
        throw new Error("Couldn't get message from gateway");
      } else if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Gateway error: ${JSON.stringify(errorData)}`);
      }
      
      // Handle streaming
      let streamedText = "";
      
      console.log("Beginning to read message stream");
      
      try {
        // Create a reader directly from the stream
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        
        // Read directly from the stream
        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            console.log("Stream reading complete");
            break;
          }
          
          // Decode the value
          const chunk = decoder.decode(value, { stream: true });
          
          // Process the chunk with cleanGatewayResponse
          try {
            const cleanedChunk = cleanGatewayResponse(value);
            if (cleanedChunk.chunk) {
              streamedText += cleanedChunk.chunk;
              
              // Update the regeneration state with the streamed content
              setRegeneratingState(prev => ({
                ...prev,
                streamContent: streamedText
              }));
            }
          } catch (chunkError) {
            console.error("Error processing chunk:", chunkError);
          }
        }
      } catch (streamError) {
        console.error("Error reading stream:", streamError);
      }
      
      console.log(`Finished streaming, got ${streamedText.length} characters of content`);
      
      // Create a NEW message with the regenerated content
      if (streamedText) {
        console.log("Creating new message with regenerated content");
        try {
          // IMPORTANT: First clear the regeneration state BEFORE adding the new message
          // This prevents both messages from appearing simultaneously
          setRegeneratingState({
            isRegenerating: false,
            streamContent: "",
            regeneratedContent: streamedText
          });
          
          // Small delay before adding the real message to allow UI to update
          await new Promise(resolve => setTimeout(resolve, 50));
          
          // Create a completely new message
          const createdMessage = await addMessageApi.mutateAsync({
            accessToken: accessToken,
            chatMessage: {
              conversationId: conversationId ?? "",
              id: "",  // New ID will be assigned by the server
              role: MessageRole.Assistant,
              content: streamedText,
              updated: new Date(),
              model: selectedModel,
              hasFeedback: false
            }
          });
          
          console.log("New message created successfully");
          
          // Force a complete refresh to ensure UI is updated with everything
          await queryClient.invalidateQueries({
            queryKey: ["chatMessageList", conversationId],
          });
          
          // Wait for the data to refresh
          await persistenceChatMessages.refetch();
          
          console.log("Messages refreshed with new content");
        } catch (createError) {
          console.error("Error creating new message:", createError);
          handleSnackbarOpen("Error creating new message");
        }
      } else {
        console.error("No content was streamed - cannot save empty message");
        handleSnackbarOpen("No content received from model");
        
        // Reset regenerating state
        console.log("Resetting regeneration state");
        setRegeneratingState({
          isRegenerating: false,
          streamContent: "",
          regeneratedContent: null
        });
      }
    } catch (error) {
      console.error("Failed to regenerate message:", error);
      handleSnackbarOpen("Failed to regenerate message");
    } finally {
      console.log("Clearing regeneration lock");
      regenerating.current = false;
      messageBeingRegeneratedId.current = null;
      messageBeingRegeneratedContent.current = null;
    }
  }, [conversationId, persistenceChatMessages.data, accessToken, queryClient, currentModel]);

  // Define composeBarHeight before it's used in renderChatMessages
  const composeBarHeight = 64;

  // Memoize the feedback handler too
  const memoizedHandleFeedback = useCallback(async (
    messageId: string,
    feedback: boolean,
    details: string,
    accessToken: OidcAccessToken,
  ): Promise<void> => {
    return handleFeedback(messageId, feedback, details, accessToken);
  }, [handleFeedback]);

  // Create a reusable notification component for consistent styling
  const StatusNotification = useCallback(({ message, show }: { message: string, show: boolean }) => {
    if (!show) return null;
    
    return (
      <Box sx={{ 
        position: 'fixed', 
        bottom: composeBarHeight + 35, // Increased margin from bottom
        right: 20,
        display: 'flex',
        alignItems: 'center',
        bgcolor: 'background.paper',
        p: 1,
        borderRadius: 1,
        boxShadow: 2,
        zIndex: 1100, // Ensure it's above other content
      }}>
        <CircularProgress size={20} sx={{ mr: 1 }} />
        <span>{message}</span>
      </Box>
    );
  }, [composeBarHeight]);

  // Update the renderChatMessages function to better handle message transitions
  const renderChatMessages = useCallback(() => {
    if (!persistenceChatMessages.data) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            placeItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "50vh",
          }}
        >
          <CircularProgress />
        </Box>
      );
    }
    
    if (persistenceChatMessages.data.length === 0) {
      return (
        <Alert severity="info" sx={{ m: "auto" }}>
          Send a message to begin the conversation.
        </Alert>
      );
    }

    // Make a defensive copy of messages
    let messages = [...persistenceChatMessages.data];

    // Calculate a unique signature for the latest assistant message (if any)
    let latestAssistantMessageSignature = "";
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === MessageRole.Assistant) {
        // Create a signature based on content (first 50 chars) to help identify similar messages
        latestAssistantMessageSignature = `${messages[i].role}:${messages[i].content.substring(0, 50)}`;
        break;
      }
    }
    
    // During regeneration, filter out the message being regenerated
    if (regenerating.current) {
      messages = messages.filter(msg => {
        // Remove message if it matches the regenerating ID (if we have it)
        if (messageBeingRegeneratedId.current && msg.id === messageBeingRegeneratedId.current) {
          return false;
        }
        
        // Also filter by content if available 
        if (messageBeingRegeneratedContent.current && 
            msg.role === MessageRole.Assistant && 
            msg.content === messageBeingRegeneratedContent.current) {
          return false;
        }
        
        return true;
      });
    }
    
    // Create array for final messages to display
    let messagesToDisplay = [...messages];
    
    // If we have a streaming content during regeneration, add it
    if (regeneratingState.isRegenerating && regeneratingState.streamContent) {
      // Find the position to insert the streaming response
      let insertIndex = messages.length;
      
      // Find the last user message to know where to put the streaming response
      for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].role === MessageRole.User) {
          insertIndex = i + 1;
          break;
        }
      }
      
      // Create a temporary message with the streaming content
      const streamingMessage = {
        id: "regenerating-temp",
        conversationId: conversationId ?? "",
        role: MessageRole.Assistant,
        content: regeneratingState.streamContent,
        updated: new Date(),
        model: currentModel,
        hasFeedback: false
      };
      
      // Create a signature for the streaming message to check for duplicates
      const streamingSignature = `${streamingMessage.role}:${streamingMessage.content.substring(0, 50)}`;
      
      // Only add the streaming message if we don't already have a similar message
      // This prevents the duplicate message issue
      if (latestAssistantMessageSignature !== streamingSignature) {
        // Insert the streaming message at the right position
        messagesToDisplay.splice(insertIndex, 0, streamingMessage);
      }
    }
    
    return (
      <>
        <ChatHistory
          chatMessages={messagesToDisplay}
          stream={stream}
          onRegenerateMessage={handleRegeneratePersistenceMessage}
          heightOffset={composeBarHeight}
          model={currentModel}
          defaultSystemPrompt={defaultSystemPrompt}
          handleFeedback={memoizedHandleFeedback}
        />
        <StatusNotification 
          message="Regenerating response..." 
          show={regeneratingState.isRegenerating} 
        />
        <StatusNotification 
          message="Generating response..." 
          show={isSending && !regeneratingState.isRegenerating} 
        />
      </>
    );
  }, [
    persistenceChatMessages.data,
    regeneratingState.isRegenerating,
    regeneratingState.streamContent,
    conversationId,
    currentModel,
    composeBarHeight,
    defaultSystemPrompt,
    handleRegeneratePersistenceMessage,
    memoizedHandleFeedback,
    stream,
    isSending,
    StatusNotification
  ]);

  // Update the handleConversationSettingsSave to minimize state changes
  const handleConversationSettingsSave = useCallback((
    newSettings: IConversationSettings,
  ) => {
    console.log(`Saving conversation settings for ${conversationId} with model:`, newSettings.model.internal_name);
    
    // IMPORTANT: Store the override ONLY for this specific conversation and only if it changed
    let modelChanged = false;
    if (conversationId && newSettings.model) {
      const currentOverride = modelOverridesByConversation.current.get(conversationId);
      if (!currentOverride || currentOverride.internal_name !== newSettings.model.internal_name) {
        // Only update if the model actually changed
        modelOverridesByConversation.current.set(conversationId, newSettings.model);
        console.log(`Set model override for conversation ${conversationId} to ${newSettings.model.internal_name}`);
        modelChanged = true;
      }
    }
    
    let currentConversation = conversations?.find(
      (c) => c.id === conversationId,
    );
    
    const systemPromptChanged = 
      currentConversation?.instructions !== newSettings.systemPrompt;
    
    if (currentConversation === undefined) {
      // Handle case where conversation hasn't been fully loaded/created yet
      console.log("Creating new settings for conversation:", conversationId);
      
      if (!conversationId) {
        console.error("Cannot save settings: invalid conversation ID");
        return;
      }
      
      // Create a new thread with the updated settings
      const newThread: PersistenceThread = {
        id: conversationId,
        name: "Unnamed Conversation",
        model: newSettings.model.internal_name,
        instructions: newSettings.systemPrompt,
        rag: newSettings.rag,
        pii: newSettings.pii,
      };
      
      // Create or update the thread
      PersistenceAPI.updateThread(accessToken.accessToken, conversationId, newThread)
        .then(result => {
          console.log("Created new thread settings:", result);
          
          // Only invalidate the list, not the messages
          queryClient.invalidateQueries({ queryKey: ["paginatedConversationList"] });
          
          // Update the system message prompt only if it changed
          if (systemPromptChanged) {
            updatePersistenceSystemPrompt(newSettings.systemPrompt);
          }
        })
        .catch(error => {
          console.error("Error creating new thread settings:", error);
        });
      
      return;
    }
    
    // Create updated thread with new settings
    const updatedThread = {
      ...currentConversation,
      name: currentConversation.name ?? "Unnamed Conversation",
      model: newSettings.model.internal_name,
      instructions: newSettings.systemPrompt,
      rag: newSettings.rag,
      pii: newSettings.pii,
    };
    
    // Only log model changes
    if (modelChanged) {
      console.log("Updating thread with model:", updatedThread.model);
    }
    
    // Update the thread in the backend
    editConversationApi
      .mutateAsync({
        accessToken: accessToken,
        editedThread: updatedThread,
      })
      .then(result => {
        console.log("Thread update result:", result ? "success" : "failed");
        
        // Update the system message prompt only if it changed
        if (systemPromptChanged) {
          return updatePersistenceSystemPrompt(newSettings.systemPrompt);
        }
      })
      .catch(error => {
        console.error("Error updating conversation settings:", error);
      });
  }, [conversationId, conversations, accessToken, queryClient, updatePersistenceSystemPrompt]);

  const handleConversationSettingsOpen = () => {
    setConversationSettingsOpen(true);
  };

  const handleConversationSettingsClose = () => {
    setConversationSettingsOpen(false);
  };

  const handleExportTranscript = () => {
    //onTranscriptDownloaded(chatMessages ?? []);
    onTranscriptDownloaded(persistenceChatMessages.data ?? []);
  };

  useEffect(() => {
    if (persistenceChatMessages.data?.length == 0) {
      // Only log once when adding the system message (and avoid duplicate logging)
      if (isFirstSettingsRender.current) {
        console.log("Adding initial system message to new conversation");
        isFirstSettingsRender.current = false;
      }
      
      // First get current conversation settings with any custom model selection that might have been made
      const settings = currentConversationSettings();
      
      // Add the system message - don't log this operation
      addMessageApi.mutate({
        accessToken: accessToken,
        chatMessage: {
          conversationId: conversationId ?? "",
          role: MessageRole.System,
          id: "",
          content: defaultSystemPrompt,
          updated: new Date(),
          model: settings.model ?? getDefaultModelFromList(availableModels),
          hasFeedback: false,
        },
      });
      
      // Only update thread model if there's an actual conversation
      if (conversationId && settings.model && conversations?.some(c => c.id === conversationId)) {
        // Find the current conversation
        let currentConversation = conversations?.find(
          (c) => c.id === conversationId,
        );
        
        if (currentConversation) {
          // Avoid excessive logging for this common operation
          // console.log("Updating thread model to:", settings.model.internal_name);
          
          // Update the thread with the selected model, but don't try to name it yet
          editConversationApi.mutate({
            accessToken: accessToken,
            editedThread: {
              ...currentConversation,
              name: currentConversation.name ?? "Unnamed Conversation", // Keep the current name
              model: settings.model.internal_name,
              instructions: currentConversation.instructions ?? defaultSystemPrompt,
              rag: currentConversation.rag ?? [],
              pii: currentConversation.pii ?? false,
            },
          });
        }
      }
    }
  }, [persistenceChatMessages.data, conversationId, conversations, accessToken]);

  const cancelStream = useCallback(() => {
    setStream(undefined);
    clearSending();
    if (abort != null) {
      try {
        abort();
      } catch (error) {
        console.error("Aborted.");
      }
    }
  }, [clearSending, abort]);

  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleSnackbarOpen = (notificationText: string) => {
    setSnackbarText(notificationText);
    setSnackbarOpen(true);
  };

  // Add a useEffect to detect and clear stream when done
  useEffect(() => {
    // This effect will run when streaming completes
    // If we have a stream and sending status is false, it means streaming finished but stream wasn't cleared
    if (stream && !isSending) {
      console.log("Cleanup: Stream detected after sending completed - resetting stream");
      setStream(undefined);
    }
  }, [stream, isSending, setStream]);
  
  // Add this function to generate a title from a user message
  const generateTitleFromMessage = (message: string): string => {
    // Take the first few words (up to 5) of the message for a title
    const words = message.trim().split(/\s+/);
    const titleWords = words.slice(0, 5);
    let title = titleWords.join(' ');
    
    // Add ellipsis if truncated
    if (words.length > 5) {
      title += '...';
    }
    
    // Limit total title length
    if (title.length > 50) {
      title = title.substring(0, 47) + '...';
    }
    
    // Ensure we have a title with at least some content and properly capitalize it
    return title.length > 0 ? capitalizeConversationTitle(title, true) : "New Conversation";
  }

  const handlePersistenceMessageSent = useCallback<ComposeAreaProps["onSend"]>(
    async (newMessage: IChatMessage | undefined) => {
      let tr = apm.startTransaction("generate", "llm.message");
      let sp = tr?.startSpan("message");
      
      // Always get the latest settings to ensure we use the current model
      const conversationSettings = currentConversationSettings();
      console.log("Message sent using model:", conversationSettings.model.internal_name);
      
      streamedContentRef.current = "";
      if (persistenceChatMessages.data === undefined) {
        throw new Error("chatMessages is undefined");
      }

      // Check if this is the first user message in this conversation
      const isFirstUserMessage = persistenceChatMessages.data === undefined ||
        persistenceChatMessages.data.filter(msg => msg.role === MessageRole.User).length === 0;
      
      // If it's the first message, update the thread title immediately
      if (isFirstUserMessage && conversationId && newMessage) {
        try {
          const messageTitle = generateTitleFromMessage(newMessage.content);
          console.log("Attempting to name conversation:", messageTitle);
          
          // Ensure conversationId is defined before proceeding
          if (!conversationId) {
            console.warn("Cannot update thread name: conversationId is undefined");
            // Continue with message sending even if naming fails
          } else {
            // Get the latest thread data directly from the server to ensure we have current state
            const currentThread = await PersistenceAPI.getThreadById(
              accessToken.accessToken,
              conversationId
            );
            
            if (currentThread && currentThread.name === "Unnamed Conversation") {
              // Create a complete updated thread object with all necessary fields
              const updatedThread: PersistenceThread = {
                ...currentThread,
                name: messageTitle,
                model: conversationSettings.model.internal_name,
                instructions: currentThread.instructions ?? defaultSystemPrompt,
                rag: currentThread.rag ?? [],
                pii: currentThread.pii ?? false,
                // Ensure timestamp is updated to trigger a change
                updated_at: new Date().toISOString()
              };
              
              console.log("Updating conversation with data:", {
                id: updatedThread.id,
                name: updatedThread.name,
                model: updatedThread.model
              });
              
              // First update locally for immediate UI feedback
              updateThread(updatedThread);
              
              // Send update to server with retry logic
              let updateAttempts = 0;
              const maxAttempts = 3;
              let updateSuccessful = false;
              
              while (updateAttempts < maxAttempts && !updateSuccessful) {
                try {
                  updateAttempts++;
                  // Update on the server
                  const result = await PersistenceAPI.updateThread(
                    accessToken.accessToken, 
                    conversationId, 
                    updatedThread
                  );
                  
                  if (result && result.name === messageTitle) {
                    console.log("Server confirmed thread update success:", result.name);
                    updateSuccessful = true;
                    
                    // Update the local state with confirmed server data
                    updateThread(result);
                    // Force a refresh of the conversation list UI
                    forceRefresh();
                  } else {
                    console.warn("Thread update verification failed, server returned:", result);
                    // Add small delay before retry
                    await new Promise(resolve => setTimeout(resolve, 500));
                  }
                } catch (error) {
                  console.error(`Error updating thread (attempt ${updateAttempts}/${maxAttempts}):`, error);
                  // Add small delay before retry
                  await new Promise(resolve => setTimeout(resolve, 500));
                }
              }
              
              // At this point, whether successful or not, schedule a verification check
              setTimeout(async () => {
                try {
                  // Verify the update was persisted by fetching fresh data
                  const verifiedThread = await PersistenceAPI.getThreadById(
                    accessToken.accessToken,
                    conversationId
                  );
                  
                  if (verifiedThread && verifiedThread.name === messageTitle) {
                    console.log("Thread name persistence verified:", verifiedThread.name);
                  } else if (verifiedThread) {
                    console.warn("Thread name mismatch - expected:", messageTitle, "got:", verifiedThread.name);
                    
                    // One final attempt if the first update failed
                    if (verifiedThread.name === "Unnamed Conversation") {
                      console.log("Making final attempt to update thread name");
                      
                      try {
                        const finalUpdatedThread = {
                          ...verifiedThread,
                          name: messageTitle,
                          updated_at: new Date().toISOString()
                        };
                        
                        const finalResult = await PersistenceAPI.updateThread(
                          accessToken.accessToken,
                          conversationId,
                          finalUpdatedThread
                        );
                        
                        if (finalResult && finalResult.name === messageTitle) {
                          console.log("Final update successful");
                          updateThread(finalResult);
                          forceRefresh();
                        }
                      } catch (finalError) {
                        console.error("Final update attempt failed:", finalError);
                      }
                    }
                  }
                } catch (verifyError) {
                  console.error("Error verifying thread update:", verifyError);
                }
              }, 2000); // Check after 2 seconds
            }
          }
        } catch (error) {
          console.error("Error in conversation naming:", error);
          // Continue with message sending even if naming fails
        }
      }
      
      // Always ensure we're using the latest model from conversation settings
      const selectedModel = conversationSettings.model || getDefaultModelFromList(availableModels);
      
      // If regenerating, ensure we remove the last assistant message from the copy
      let copied = persistenceChatMessages.data[persistenceChatMessages.data.length - 1].role === MessageRole.Assistant 
        ? persistenceChatMessages.data.slice(0, persistenceChatMessages.data.length - 1) 
        : persistenceChatMessages.data;
      
      // Log if this is a regeneration
      if (newMessage === undefined) {
        console.log("Regenerating response with model:", selectedModel.internal_name);
      }
      
      // Reset stream state before starting a new request
      setStream(undefined);
      
      const assistantMessagePromise = !selectedModel.image_model
        ? sendGatewayRequest(
            newMessage === undefined
              ? copied
              : [...copied, newMessage],
            selectedModel,
            accessToken,
            conversationSettings.rag,
            conversationSettings.pii,
            chatOptions.show_rag,
            handleSnackbarOpen,
          )
        : sendImageGatewayRequest(
            newMessage === undefined
              ? copied
              : [...copied, newMessage],
            selectedModel,
            accessToken,
            conversationSettings.rag,
            conversationSettings.pii,
            handleSnackbarOpen,
          );
      abortControllerRef.current = assistantMessagePromise.abort;
      
      setSending();
      
      if (newMessage !== undefined) {
        await addMessageApi.mutateAsync({
          accessToken: accessToken,
          chatMessage: newMessage,
        });
      }
      
      try {
        const response = await assistantMessagePromise;
        
        if (response.body === null) {
          throw new Error("Couldn't get message from gateway");
        } else if (!response.ok) {
          const errorData = await response.json();
          throw new Error(`Gateway error: ${JSON.stringify(errorData)}`);
        }
        
        // Create two streams from the response
        const [streamForUI, streamForProcessing] = response.body.tee();
        
        // Set the stream for UI rendering
        setStream(streamForUI);
        
        // Create a flag to track if we're still streaming content
        const isStreamingRef = { current: true };
        
        // Process the message stream for persistence
        const messageContent = await readMessageStream(streamForProcessing, (chunk, isLastChunk) => {
          // Update streamed content
          updateStreamedMessageText(chunk);
          
          // No need to do any naming-related checks when streaming completes
          if (isLastChunk) {
            console.log("Message stream complete - final content ready");
            isStreamingRef.current = false;
          }
        });
        
        // Create a assistantChatMessage with the final content
        const assistantChatMessage = {
          id: "!!TEMP",
          conversationId: conversationId ?? "",
          role: MessageRole.Assistant,
          content: messageContent.chunks,
          citations: messageContent.citations,
          updated: new Date(),
          model: selectedModel,
          hasFeedback: false,
        };
        
        // IMPORTANT: Clear the stream BEFORE adding the message to prevent duplicate display
        // This prevents the UI showing both the stream and the persisted message simultaneously
        setStream(undefined);
        
        // Small delay to allow UI to update before persisting
        await new Promise(resolve => setTimeout(resolve, 50));
        
        // Add the message to persistence
        const persisted = await addMessageApi.mutateAsync({
          accessToken: accessToken,
          chatMessage: assistantChatMessage,
        });
        
        // Make sure thread is updated with the latest timestamp
        if (conversationId) {
          const currentConversation = conversations?.find(c => c.id === conversationId);
          if (currentConversation) {
            // Log the current state to help diagnose if thread name is already correct
            console.log(`Updating thread timestamp with existing name: ${currentConversation.name}`);
            
            // Ensure we're preserving the existing name when updating the timestamp
            editConversationApi.mutate({
              accessToken: accessToken,
              editedThread: {
                ...currentConversation,
                // Explicitly keep the existing name, never reset to "Unnamed Conversation"
                name: currentConversation.name,
                updated_at: new Date().toISOString()
              }
            });
          }
        }
        
        return persisted;
        
      } catch (err) {
        console.error("Error in message processing:", err);
        
        // Create an error message and persist it
        const errorMessage = {
          id: "!!TEMP",
          conversationId: conversationId ?? "",
          role: MessageRole.Assistant,
          content: err instanceof Error ? err.toString() : String(err),
          updated: new Date(),
          model: selectedModel,
          hasFeedback: false,
        };
        
        await addMessageApi.mutateAsync({
          accessToken: accessToken,
          chatMessage: errorMessage,
        });
        
        return errorMessage;
      } finally {
        clearSending();
        setStream(undefined);
        if (sp) sp.end();
        if (tr) tr.end();
      }
    },
    [
      persistenceChatMessages.data,
      conversationId,
      accessToken,
      currentConversationSettings,
      availableModels,
      chatOptions.show_rag,
      addMessageApi,
      conversations,
      updateThread,
      clearSending,
      setSending,
      editConversationApi,
      setStream,
    ]
  );

  // Add back the filtering effect that was accidentally removed
  useEffect(() => {
    if (conversationId && persistenceChatMessages.data) {
      // Only apply filtering if we have something to filter
      if (deletedMessageIds.current.size > 0 || deletedMessagesContent.current.size > 0) {
        console.log("Checking if messages need filtering...");
        
        // Check if any messages being displayed should be filtered out
        const shouldBeFiltered = persistenceChatMessages.data.some(
          msg => deletedMessageIds.current.has(msg.id) || 
                (msg.role === MessageRole.Assistant && deletedMessagesContent.current.has(msg.content))
        );
        
        if (shouldBeFiltered) {
          // Filter out any messages that were marked as deleted - by ID OR content
          const filteredMessages = persistenceChatMessages.data.filter(
            msg => !deletedMessageIds.current.has(msg.id) && 
                  !(msg.role === MessageRole.Assistant && deletedMessagesContent.current.has(msg.content))
          );
          
          console.log(`Filtering out ${persistenceChatMessages.data.length - filteredMessages.length} messages that match deletion criteria`);
          
          // Update the query data with our filtered list
          queryClient.setQueryData(
            ["chatMessageList", conversationId], 
            filteredMessages
          );
        }
      }
    }
  }, [persistenceChatMessages.data, conversationId, queryClient]);

  // Add back the localStorage effect that was accidentally removed
  useEffect(() => {
    // Clear tracked deleted messages when changing conversations
    deletedMessageIds.current.clear();
    deletedMessagesContent.current.clear();
    
    if (conversationId) {
      // Load any previously tracked messages from localStorage
      loadDeletedMessages(conversationId);
    }
    
    console.log("Cleared in-memory tracking and loaded from storage for new conversation");
  }, [conversationId, loadDeletedMessages]);

  // Add back the useEffect that was accidentally removed
  useEffect(() => {
    if (paginatedConversationList.error) {
      onLoadingFailed();
    }
  }, [paginatedConversationList.error, onLoadingFailed]);

  if (conversationId == null) {
    return (
      <>
        <Toolbar />
        <Alert
          severity="info"
          sx={{ ml: "auto", mr: "auto", mt: "1em", width: "fit-content" }}
        >
          Select or add a conversation to begin.
        </Alert>
      </>
    );
  }

  return (
    <>
      <Toolbar />
      <Box component="div">
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          message={snackbarText}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          action={
            <Fragment>
              <IconButton
                aria-label="close"
                color="inherit"
                sx={{ p: 0.5 }}
                onClick={handleSnackbarClose}
              >
                <Close />
              </IconButton>
            </Fragment>
          }
        />
        {persistenceChatMessages.isFetching &&
        (persistenceChatMessages.data ?? []).length == 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              placeItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "50vh",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          renderChatMessages()
        )}

        <Box
          position="fixed"
          component="div"
          sx={{
            boxSizing: "border-box",
            minHeight: composeBarHeight,
            maxHeight: `calc(${composeBarHeight}px * 3)`,
            bottom: 0,
            right:
              formFactor > FormFactor.Tablet && chatOptions.use_promotions
                ? "280px"
                : 0,
            left: sidebarOpen ? sidebarWidth : 0,
            margin: 0,
            backgroundColor: darkModeEnabled ? "black" : "white",
            overflow: "none",
          }}
        >
          <ComposeArea
            onSend={handlePersistenceMessageSent}
            enabled={!isSending && !regeneratingState.isRegenerating}
            onConversationSettingsOpen={handleConversationSettingsOpen}
            onExportTranscript={handleExportTranscript}
            onError={handleSnackbarOpen}
            conversationId={conversationId}
            cancelStream={cancelStream}
          />
        </Box>
        <ConversationSettings
          open={conversationSettingsOpen}
          onClose={handleConversationSettingsClose}
          onSave={handleConversationSettingsSave}
          currentRag={currentConversationSettings().rag}
          currentPii={currentConversationSettings().pii}
          currentModel={currentConversationSettings().model}
          currentSystemPrompt={currentConversationSettings().systemPrompt}
          defaultSystemPrompt={defaultSystemPrompt}
        />
      </Box>
    </>
  );
}
